import React, { useState } from "react";
import Modal from "../components/Modal";
import Button from "../components/Buttons";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import WorkflowStepPanel from "./WorkflowStepPanel";
import { useRemediationWorkflows } from "../contexts/FeatureFlagsContext";

const RiskManagementModal = ({ risk, onClose, onUpdate, onAccept, refreshData, updateEndpoint, fieldPrefix }) => {
  // workflowState: "unknown" until WorkflowStepPanel reports its initial load, then "active"
  // (the remediation has an Active workflow: status controls are unmounted) or "none", or
  // "finished" once a workflow seen active in this session ends (controls stay unmounted). The
  // status select / progress slider mount only when "none", so they never flash before the
  // workflow state is known; Update is disabled while "unknown". workflowLocked: a workflow was
  // active at any point in this modal session, so the submit must never write the modal's stale
  // local status/progress over the engine's values.
  // Features:RemediationWorkflows (UI-only): off (or no remediation) = the panel is never mounted,
  // so the state starts at "none" and the status / progress controls behave exactly as before.
  const remediationWorkflows = useRemediationWorkflows();
  const [workflowState, setWorkflowState] = useState(
    remediationWorkflows && risk.remediationID ? "unknown" : "none"
  );
  const [workflowLocked, setWorkflowLocked] = useState(false);
  const [workflowEndState, setWorkflowEndState] = useState(null);
  const [newRating, setNewRating] = useState(
    (risk.userRating || risk.priority || risk.inherentRiskRating || '').toUpperCase()
  );
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState(null);
  const [fileError, setFileError] = useState("");
  const [remediationStatus, setRemediationStatus] = useState(
    risk.remediationStatus || "Not Started"
  );
  const [remediationPlan, setRemediationPlan] = useState(
    risk.remediation_plan || ""
  );
  const [progressPercentage, setProgressPercentage] = useState(
    risk.progressPercentage || 0
  );
  const [remediationTargetDate, setRemediationTargetDate] = useState(
    risk.remediationTargetDate
      ? risk.remediationTargetDate.split("T")[0]
      : ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [apiError, setApiError] = useState("");

  // Capture original rating at mount (const, not state — never changes across submits)
  const originalRating = (risk.userRating || risk.priority || risk.inherentRiskRating || '').toUpperCase();

  // Tracks the "from" status for the next StatusChange log entry.
  const [previousStatusForLog, setPreviousStatusForLog] = useState(
    risk.remediationStatus || "Not Started"
  );

  const apiCall = useApi();
  const { user } = useAuth();

  const handleUpdateRiskStatus = async () => {
    setValidationError("");
    setApiError("");
    if (!reason.trim()) {
      setValidationError("Reason for Change is required.");
      return;
    }
    setIsSubmitting(true);
    try {
      const endpoint = updateEndpoint || `/risks/${risk.riskId}/update-status`;
      const p = fieldPrefix || "";
      const payload = {};
      payload[`${p}UserRating`] = newRating;
      payload[`${p}UserReason`] = reason;
      if (!workflowLocked) {
        payload[`${p}RemediationStatus`] = remediationStatus;
        payload[`${p}ProgressPercentage`] = progressPercentage;
      }
      payload[`${p}RemediationTargetDate`] = remediationTargetDate
        ? new Date(remediationTargetDate).toISOString()
        : null;
      payload[`${p}ActionDescription`] = remediationPlan;
      await apiCall("PUT", endpoint, payload);

      // Write logs only for values that actually changed
      if (risk.remediationID && risk.remediationType) {
        const statusChanged = remediationStatus !== previousStatusForLog;
        const ratingChanged = (newRating || "").toUpperCase() !== (originalRating || "").toUpperCase();
        const hasEvidence = evidence && evidence instanceof File;

        if (statusChanged) {
          try {
            const logData = {
              remediationType: risk.remediationType,
              previousStatus: previousStatusForLog,
              newStatus: remediationStatus,
              comment: reason,
              changedByUserName: user?.userName || user?.email || "",
              changeType: "StatusChange",
              riskId: risk.riskId,
              targetDate: remediationTargetDate ? new Date(remediationTargetDate).toISOString() : null,
            };

            if (hasEvidence) {
              const formData = new FormData();
              Object.entries(logData).forEach(([key, value]) => {
                if (value != null) formData.append(key, value);
              });
              formData.append("file", evidence);
              await apiCall("POST", "/allRemediations/logs/by-risk/with-evidence", formData, true);
            } else {
              await apiCall("POST", "/allRemediations/logs/by-risk", logData);
            }
            setPreviousStatusForLog(remediationStatus);
          } catch (logErr) {}
        }

        if (ratingChanged) {
          try {
            const logData = {
              remediationType: risk.remediationType,
              previousStatus: originalRating,
              newStatus: newRating,
              comment: statusChanged ? "" : reason,
              changedByUserName: user?.userName || user?.email || "",
              changeType: "RatingChange",
              riskId: risk.riskId,
              targetDate: remediationTargetDate ? new Date(remediationTargetDate).toISOString() : null,
            };

            // Attach evidence to rating change only if status didn't change (avoid duplicate upload)
            if (hasEvidence && !statusChanged) {
              const formData = new FormData();
              Object.entries(logData).forEach(([key, value]) => {
                if (value != null) formData.append(key, value);
              });
              formData.append("file", evidence);
              await apiCall("POST", "/allRemediations/logs/by-risk/with-evidence", formData, true);
            } else {
              await apiCall("POST", "/allRemediations/logs/by-risk", logData);
            }
          } catch (ratingLogErr) {}
        }

        // Upload evidence even when neither status nor rating changed
        if (hasEvidence && !statusChanged && !ratingChanged) {
          try {
            const formData = new FormData();
            formData.append("remediationType", risk.remediationType);
            formData.append("previousStatus", remediationStatus);
            formData.append("newStatus", remediationStatus);
            formData.append("comment", reason);
            formData.append("changedByUserName", user?.userName || user?.email || "");
            formData.append("changeType", "EvidenceUpload");
            formData.append("riskId", risk.riskId);
            if (remediationTargetDate) {
              formData.append("targetDate", new Date(remediationTargetDate).toISOString());
            }
            formData.append("file", evidence);
            await apiCall("POST", "/allRemediations/logs/by-risk/with-evidence", formData, true);
          } catch (evidenceErr) {}
        }
      }

      // Awaited so a reopened dialog is built from the saved values, not the old snapshot (AX-238).
      if (refreshData) {
        try { await refreshData(); } catch { /* the save succeeded; the list just stays stale */ }
      }
      onClose();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update risk. Please try again.";
      setApiError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Manage Risk" size="2xl">
      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-lg">{risk.riskTitle}</h3>
          <p className="text-sm text-gray-600 mt-2">{risk.detailedRiskDescription}</p>
        </div>

        {/* Monetary Impact Analysis Section */}
        {risk.monetary_value && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
              💰 Monetary Impact Analysis - $
              {(risk.estimatedMonetaryImpact || 0).toLocaleString()}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h5 className="font-medium text-blue-800">Cost Breakdown:</h5>
                <div className="text-sm space-y-1">
                  {[
                    { label: "Direct Costs (25%)", value: 0.25 },
                    { label: "Business Interruption (30%)", value: 0.3 },
                    { label: "Regulatory Fines (20%)", value: 0.2 },
                    { label: "Reputation Damage (15%)", value: 0.15 },
                    { label: "Recovery Costs (10%)", value: 0.1 },
                  ].map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-gray-700">{item.label}:</span>
                      <span className="font-medium">
                        ${Math.round(risk.estimatedMonetaryImpact * item.value).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <h5 className="font-medium text-blue-800">Analysis Details:</h5>
                <div className="text-sm space-y-1 text-gray-700">
                  <div><strong>Methodology:</strong> Monte Carlo simulation with industry benchmarks</div>
                  <div><strong>Confidence Level:</strong> Medium (Historical data + Expert assessment)</div>
                  <div><strong>Time Horizon:</strong> 12-month risk exposure window</div>
                  <div><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</div>
                </div>
              </div>
            </div>
            <div className="border-t border-blue-200 pt-3 mt-3">
              <h5 className="font-medium text-blue-800 mb-2">Risk-Specific Factors:</h5>
              <div className="text-xs text-gray-600 space-y-1">
                <div>
                  <strong>Severity Multiplier:</strong>{" "}
                  {newRating === "CRITICAL"
                    ? "1.5x (Critical risk amplification)"
                    : newRating === "HIGH"
                    ? "1.2x (High impact scaling)"
                    : newRating === "MEDIUM"
                    ? "1.0x (Baseline calculation)"
                    : "0.7x (Lower probability adjustment)"}
                </div>
                <div><strong>Industry Context:</strong> Financial services sector with elevated regulatory and reputational exposure</div>
                <div><strong>Data Classification:</strong> Impact scaled based on sensitive customer data and financial information exposure</div>
                <div><strong>Business Criticality:</strong> Assessment considers application criticality and operational dependency</div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Rating */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Risk Rating
          </label>
          <select
            value={newRating}
            onChange={(e) => setNewRating(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
          >
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
            <option value="INFORMATIONAL">Informational</option>
          </select>
        </div>

        {/* Remediation Status */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Remediation Status
          </label>
          {remediationWorkflows && risk.remediationID ? (
            <div className="mb-3">
              <WorkflowStepPanel
                riskId={risk.riskId}
                remediationId={risk.remediationID}
                remediationTitle={risk.riskTitle}
                dealId={risk.dealId}
                businessUnitId={risk.businessUnitId}
                onChanged={() => refreshData && refreshData()}
                onWorkflowStateChange={(active, instance) => {
                  // A workflow that ends in this session has already written the status; the controls
                  // must not come back holding the values captured at open (AX-238). They stay hidden
                  // ("finished") because submit never sends status/progress once locked.
                  if (!active && workflowLocked) {
                    if (instance?.state === "Completed") {
                      setRemediationStatus("Completed");
                      setProgressPercentage(100);
                      setPreviousStatusForLog("Completed");
                    }
                    setWorkflowState("finished");
                    setWorkflowEndState(instance?.state ?? null);
                    return;
                  }
                  setWorkflowState(active ? "active" : "none");
                  if (active) setWorkflowLocked(true);
                }}
              />
            </div>
          ) : null}
          {workflowState === "active" && (
            <p className="text-sm text-content-secondary">Status is managed by the workflow</p>
          )}
          {workflowState === "finished" && (
            <p className="text-sm text-content-secondary">
              {workflowEndState === "Completed"
                ? "Completed by the workflow (100%)"
                : "The workflow has ended. Close and reopen this dialog to change the status."}
            </p>
          )}
          {workflowState === "none" && (
          <select
            value={remediationStatus}
            onChange={(e) => {
              const status = e.target.value;
              setRemediationStatus(status);
              const autoProgress = {
                "Not Started": 0,
                "Planned": 20,
                "In Progress": 40,
                "Under Review": 75,
                "Completed": 100,
              };
              if (autoProgress[status] !== undefined) {
                setProgressPercentage(autoProgress[status]);
              }
            }}
            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
          >
            <option value="Not Started">Not Started</option>
            <option value="Planned">Planned</option>
            <option value="In Progress">In Progress</option>
            <option value="Under Review">Under Review</option>
            <option value="Completed">Completed</option>
          </select>
          )}
        </div>

        {/* Progress Percentage (mounted only once the workflow state is known to be none) */}
        {workflowState === "none" && (
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Progress Percentage: {progressPercentage}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={progressPercentage}
            onChange={(e) => setProgressPercentage(parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        )}

        {remediationStatus !== "Not Started" && (
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              Remediation Plan
            </label>
            <textarea
              value={remediationPlan}
              onChange={(e) => setRemediationPlan(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
              rows="3"
              placeholder="Describe the remediation plan..."
            />
          </div>
        )}

        {/* Reason for Change */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Reason for Change<span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (validationError) setValidationError("");
            }}
            className={`w-full px-3 py-2 border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan ${
              validationError ? "border-red-500" : "border-border"
            }`}
            rows="3"
          />
          {validationError && (
            <p className="mt-1 text-sm text-red-600">{validationError}</p>
          )}
        </div>

        {/* Remediation Target Date */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Remediation Target Date
          </label>
          <input
            type="date"
            value={remediationTargetDate}
            onChange={(e) => setRemediationTargetDate(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
          />
        </div>

        {/* Supporting Evidence */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Supporting Evidence
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xls,.xlsx"
            onChange={(e) => {
              setFileError("");
              const file = e.target.files?.[0];
              if (!file) { setEvidence(null); return; }
              const maxSize = 10 * 1024 * 1024;
              const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/png", "image/jpeg", "image/jpg",
                "text/plain",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ];
              if (file.size > maxSize) {
                setFileError("File is too large. Maximum size is 10MB.");
                e.target.value = "";
                setEvidence(null);
                return;
              }
              if (!allowedTypes.includes(file.type)) {
                setFileError(`File type "${file.type}" is not supported.`);
                e.target.value = "";
                setEvidence(null);
                return;
              }
              setEvidence(file);
            }}
            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
          />
          {fileError && (
            <p className="mt-1 text-sm text-red-600">{fileError}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Upload supporting documentation (optional, max 10MB)
          </p>
        </div>

        {/* Actions */}
        {apiError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {apiError}
          </div>
        )}
        <div className="flex justify-end space-x-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleUpdateRiskStatus} disabled={isSubmitting || workflowState === "unknown"}>
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RiskManagementModal;
